import { EntityType, calculateTimeDifferenceBetweenDates } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { LaundryTransactionClient } from '@frontend/laundry-app/api';
import { LaundryTransaction } from '@frontend/laundry-app/types';
import { TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../../redux/store';
import OverviewHeader from '../../header/overview-header';
import { useReceiveDirtyLaundry } from './receive-dirty-laundry.controller';

export const ReceiveDirtyLaundry = () => {
    const { openPlatformDetail } = usePlatformModalHost();
    const viewProps = useReceiveDirtyLaundry();

    return (
        <div
            key='receive-dirty-laundry-transactions'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='feature.receive_dirty_laundry'
                        defaultMessage='Receive Dirty Laundry'
                    />
                }
            />
            <ReactiveObjectList<LaundryTransaction>
                useStaticListId
                id='receive-dirty-laundry-transactions'
                fetch={(arg) =>
                    LaundryTransactionClient.fetchIncomingDirtyLaundry(viewProps.currentAccount, {
                        state: [TransactionStateName.TRANSACTION_PICK_UP_PENDING, TransactionStateName.TRANSACTION_IN_TRANSIT],
                        ...arg
                    })
                }
                onSelectElement={(obj) => openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.LAUNDRY } })}
                dispatch={useAppDispatch()}
                activeFilter={{
                    searchKeys: ['tracking_number', 'sender', 'product', 'user'],
                    filterKeys: ['users', 'before', 'after'],
                    displayName: new Map([
                        ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                        ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                        ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                        ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                        ['before', CommonMessage.OBJECTS.COMMON_FIELDS.BEFORE],
                        ['after', CommonMessage.OBJECTS.COMMON_FIELDS.AFTER]
                    ]),
                    queryKeyMap: new Map([['senders', 'sender_id']])
                }}
                columns={{
                    columns: ['tracking_number', 'senders', 'content', 'waiting', 'creation_timestamp'],
                    headerOverwrite: new Map([
                        ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                        ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                        ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                        ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                        ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                    ]),
                    customCells: new Map([
                        [
                            'senders',
                            (obj) => {
                                if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                        </td>
                                    );
                                }
                                return (
                                    <td
                                        key={obj.row.original.senders[0].entity_id}
                                        className={defaultCellClassName}>
                                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                                            <PlatformObjectBadge
                                                id={obj.row.original.senders[0].entity_id}
                                                entityType={EntityType.USER}
                                                display={obj.row.original.senders[0].name}
                                                key={obj.row.original.senders[0].entity_id}
                                                foreign={obj.row.original.senders[0].foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                            />
                                            {obj.row.original.senders.length > 1 && (
                                                <MultiEntityButton
                                                    entities={obj.row.original.senders.slice(1)}
                                                    render={(obj) => (
                                                        <PlatformObjectBadge
                                                            id={obj.entity_id}
                                                            entityType={EntityType.USER}
                                                            display={obj.name}
                                                            key={obj.entity_id}
                                                            foreign={obj.foreign}
                                                            accountId={accountIdFromResolvedEntity(obj)}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            }
                        ],
                        [
                            'content',
                            (obj) => {
                                if (obj.row.original.contents === null || obj.row.original.contents.length === 0) {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                        </td>
                                    );
                                }
                                const result: Record<string, number> = {};
                                obj.row.original.contents.forEach((content) => {
                                    if (!result[content.product_name]) {
                                        result[content.product_name] = 1;
                                    } else {
                                        result[content.product_name]++;
                                    }
                                });

                                return (
                                    <td
                                        key={obj.row.original.contents[0].id}
                                        className={defaultCellClassName}>
                                        <div className='inline-flex items-center justify-start gap-3'>
                                            {Object.entries(result)
                                                .slice(0, 4)
                                                .map(([key, value]) => (
                                                    <span>
                                                        {value}x {key}
                                                    </span>
                                                ))}
                                            {Object.entries(result).length > 4 && (
                                                <MultiEntityButton
                                                    placement='bottom'
                                                    entities={Object.entries(result).slice(4)}
                                                    render={(obj) => (
                                                        <span className='text-foreground-default text-sm font-medium'>
                                                            {obj[1]}x {obj[0]}
                                                        </span>
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            }
                        ],
                        [
                            'waiting',
                            (obj) => {
                                return (
                                    <td
                                        key={obj.row.original.update_timestamp}
                                        className={defaultCellClassName}>
                                        {calculateTimeDifferenceBetweenDates(obj.row.original.update_timestamp, new Date(Date.now()).toISOString())}
                                    </td>
                                );
                            }
                        ]
                    ])
                }}
                type={EntityType.TRANSACTION}
            />
        </div>
    );
};
